/**
 * The legal surfaces' approved text (M17-T17).
 *
 * SOURCE OF TRUTH IS `docs/legal/`, not this file. The `.md` there is the AMTC
 * text as written and approved; this is its render. Change the markdown first,
 * then carry it across — the same direction `index.css` follows against
 * `designs/tokens.css`, and for the same reason: legal copy is approved as
 * text, and a diff a lawyer can read must not require reading JSX.
 *
 * DO NOT EDIT THE WORDING HERE. Rewording a promise in the component layer is
 * how a product ends up publishing something nobody approved.
 */

/**
 * The organisation details the three documents end with — the data controller's
 * identity, which the Data Protection Act, 2012 (Act 843) requires a privacy
 * notice to carry.
 *
 * `null` means "not yet supplied by AMTC", and every renderer OMITS a null line
 * rather than printing a placeholder. That is deliberate: a page reading
 * "Email: [Insert email]" is worse than one with no email line, because it
 * looks like a finished document and is not.
 *
 * THE RELEASE GATE. These pages must not be linked from the footer until
 * `legalContactComplete()` is true. A privacy policy whose §10 says complaints
 * "should first be directed to Medcor using the details above" is broken if
 * there are no details above.
 */
export interface OrgDetails {
  /** Registered legal entity operating the platform. */
  legalName: string | null
  /** Registered or business address. */
  address: string | null
  /** General contact address, used by the Terms and the contact page. */
  contactEmail: string | null
  /** Privacy / data-protection address. May be the same as `contactEmail`. */
  privacyEmail: string | null
  phone: string | null
  /** Date the current version took effect, e.g. "13 August 2026". */
  effectiveDate: string | null
  /** Named data-protection contact. Optional in the source text. */
  dataProtectionSupervisor: string | null
  /** Ghana DPC registration. The source text says "insert when available". */
  dpcRegistrationNumber: string | null
}

export const ORG: OrgDetails = {
  legalName: 'Africa Medical Tourism Council',
  address: '6 Avela Lane, Awoshie, Accra',
  contactEmail: 'africamedicaltourism@gmail.com',
  privacyEmail: 'africamedicaltourism@gmail.com',
  phone: '+233 20 240 0363',
  effectiveDate: '13 August 2026',
  dataProtectionSupervisor: 'Dr Koby Appiah-Sakyi',
  // Not yet issued. Stays null so the line is omitted rather than promised —
  // the source text says "insert when available", and it is not required for
  // `legalContactComplete()`.
  dpcRegistrationNumber: null,
}

/**
 * True when every detail the documents cannot stand without is present. The
 * two optional ones — the named supervisor and the DPC registration number —
 * are excluded: the source text marks both as conditional.
 */
export function legalContactComplete(org: OrgDetails = ORG): boolean {
  return (
    org.legalName !== null &&
    org.address !== null &&
    org.contactEmail !== null &&
    org.privacyEmail !== null &&
    org.phone !== null &&
    org.effectiveDate !== null
  )
}

/** A labelled line in a document's closing contact block. */
export interface ContactLine {
  label: string | null
  value: string | null
}

export type Block =
  | { kind: 'p'; text: string }
  | { kind: 'ul'; items: string[] }
  | { kind: 'contact'; intro: string; lines: ContactLine[]; outro?: string[] }

export interface LegalSection {
  heading: string
  blocks: Block[]
}

export interface LegalDocument {
  /** Page `h1` and `<title>`. */
  title: string
  /** One-line summary under the title. Ours, not the source's. */
  standfirst: string
  /** Shown as "Effective date" when `ORG.effectiveDate` is set. */
  dated: boolean
  intro: Block[]
  sections: LegalSection[]
}

export const PRIVACY_POLICY: LegalDocument = {
  title: 'Privacy Policy',
  standfirst:
    'What Medcor collects, why, who it is shared with, and the rights you have over it.',
  dated: true,
  intro: [
    {
      kind: 'p',
      text: 'Medcor Health Network ("Medcor", "we", "us" or "our") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains what information we collect, why we collect it, how it may be used and shared, and the rights you have in relation to your information.',
    },
    {
      kind: 'p',
      text: 'We process personal data in accordance with the Data Protection Act, 2012 (Act 843) of Ghana and other applicable laws.',
    },
  ],
  sections: [
    {
      heading: '1. Information We Collect',
      blocks: [
        {
          kind: 'p',
          text: 'Depending on how you use Medcor, we may collect:',
        },
        {
          kind: 'ul',
          items: [
            'Identity and contact information, including your name, age/date of birth, sex, telephone number, email address and other information required to identify or contact you.',
            'Healthcare-related information that you voluntarily provide when searching for, requesting or arranging healthcare services, including symptoms, healthcare needs, appointment information and information necessary to connect you with an appropriate healthcare provider.',
            'Location information, where you permit us to use your location to identify nearby healthcare facilities, emergency services or other relevant services.',
            'Booking and transaction information, including appointments, service requests, payment status and transaction references. Where payments are handled by an authorised payment provider, Medcor will not intentionally store complete payment-card details unless this is specifically required and lawfully implemented.',
            'Technical information, such as device, browser, IP address, login and security information and records of how the platform is used.',
            'Communications, including enquiries, complaints, feedback and communications with Medcor.',
          ],
        },
        {
          kind: 'p',
          text: 'We will only collect information that is reasonably necessary for the services being provided.',
        },
      ],
    },
    {
      heading: '2. How We Use Your Information',
      blocks: [
        { kind: 'p', text: 'We may use your information to:' },
        {
          kind: 'ul',
          items: [
            'create and manage your account;',
            'identify and display healthcare services relevant to your request;',
            'facilitate bookings and communication with healthcare providers;',
            'provide location-based and emergency-service functionality;',
            'process or confirm transactions;',
            'send booking confirmations, reminders and important service communications;',
            'provide customer support and resolve complaints;',
            'maintain the security and reliability of Medcor;',
            'improve the platform using appropriately protected or anonymised information; and',
            'comply with legal and regulatory obligations.',
          ],
        },
        {
          kind: 'p',
          text: 'Medcor will not sell your personal or health information to advertisers or other third parties.',
        },
      ],
    },
    {
      heading: '3. Health Information',
      blocks: [
        {
          kind: 'p',
          text: 'Information concerning your health is particularly sensitive. We will only process health-related information where there is a lawful basis to do so and where it is necessary for the service requested.',
        },
        {
          kind: 'p',
          text: 'Where information needs to be provided to a healthcare provider to fulfil your request, only information reasonably necessary for that purpose should be shared.',
        },
        {
          kind: 'p',
          text: 'Medcor is principally a platform for connecting users with healthcare providers and related services. Healthcare providers remain responsible for the professional care they provide and for their own legal obligations concerning patient information and medical records.',
        },
      ],
    },
    {
      heading: '4. Sharing Your Information',
      blocks: [
        { kind: 'p', text: 'We may share relevant information with:' },
        {
          kind: 'ul',
          items: [
            'healthcare providers or facilities selected by or providing services to you;',
            'technology, communications, hosting, mapping and payment providers necessary to operate Medcor;',
            'authorised Medcor personnel who require access to perform their duties; and',
            'regulators, law-enforcement agencies or other authorities where disclosure is required or permitted by law.',
          ],
        },
        {
          kind: 'p',
          text: 'Third-party service providers processing personal data on our behalf must be subject to appropriate confidentiality, data-protection and security requirements.',
        },
        {
          kind: 'p',
          text: 'We will not disclose identifiable health information merely for advertising or promotional purposes.',
        },
      ],
    },
    {
      heading: '5. Security and Storage',
      blocks: [
        {
          kind: 'p',
          text: 'We will implement reasonable technical and organisational safeguards designed to protect personal information against unauthorised access, alteration, disclosure, loss or destruction.',
        },
        {
          kind: 'p',
          text: 'Access to sensitive information will be restricted to persons who require it for an authorised purpose.',
        },
        {
          kind: 'p',
          text: 'We will retain personal information only for as long as reasonably necessary for the purposes for which it was collected, including applicable legal, regulatory, healthcare, accounting and dispute-resolution requirements. Information that is no longer required will be securely deleted or anonymised where appropriate.',
        },
      ],
    },
    {
      heading: '6. Your Rights',
      blocks: [
        {
          kind: 'p',
          text: 'Subject to applicable Ghanaian law, you may have the right to:',
        },
        {
          kind: 'ul',
          items: [
            'know whether and how we process your personal information;',
            'request access to personal information we hold about you;',
            'request correction of inaccurate or incomplete information;',
            'object to certain processing of your information;',
            'withdraw consent where processing is based on consent; and',
            'object to the use of your personal information for direct marketing.',
          ],
        },
        {
          kind: 'p',
          text: 'Requests concerning these rights may be submitted using the contact details below.',
        },
      ],
    },
    {
      heading: '7. Location, Cookies and Similar Technologies',
      blocks: [
        {
          kind: 'p',
          text: 'Where Medcor requests access to your device location, this will be used for functions such as identifying nearby healthcare services or supporting location-dependent services. Device permissions may be changed through your device or browser settings.',
        },
        {
          kind: 'p',
          text: 'Medcor may use cookies or similar technologies where necessary for security, authentication, platform functionality, analytics and user preferences. Where legally required, users will be given appropriate choices concerning non-essential technologies.',
        },
      ],
    },
    {
      heading: '8. Children',
      blocks: [
        {
          kind: 'p',
          text: 'Where Medcor processes information relating to a child, appropriate safeguards will be applied and information should ordinarily be provided by, or with the involvement of, a parent, legal guardian or other person authorised by law, except where applicable law permits otherwise.',
        },
      ],
    },
    {
      heading: '9. Changes to this Policy',
      blocks: [
        {
          kind: 'p',
          text: "We may update this Privacy Policy when Medcor's services, technology or legal obligations change. The current version will be published on the Medcor platform with its effective date.",
        },
      ],
    },
    {
      heading: '10. Contact and Complaints',
      blocks: [
        {
          kind: 'contact',
          intro: "Medcor's data controller/operator is:",
          lines: [
            { label: null, value: ORG.legalName },
            { label: 'Address', value: ORG.address },
            { label: 'Email', value: ORG.privacyEmail },
            { label: 'Telephone', value: ORG.phone },
            {
              label: 'Data Protection Supervisor',
              value: ORG.dataProtectionSupervisor,
            },
            {
              label: 'Data Protection Commission Registration Number',
              value: ORG.dpcRegistrationNumber,
            },
          ],
          outro: [
            'Questions, requests or complaints concerning personal information should first be directed to Medcor using the details above.',
            'Individuals also have the right to raise concerns with the Data Protection Commission of Ghana where they believe their rights under applicable data-protection law have been infringed.',
          ],
        },
      ],
    },
  ],
}

export const TERMS_OF_SERVICE: LegalDocument = {
  title: 'Terms of Service',
  standfirst:
    'The terms you agree to by using Medcor, and what the platform does and does not promise.',
  dated: true,
  intro: [
    {
      kind: 'p',
      text: 'These Terms of Service ("Terms") govern your access to and use of the Medcor website, application and related services ("Medcor" or the "Platform"). By using Medcor, you agree to these Terms.',
    },
  ],
  sections: [
    {
      heading: '1. About Medcor',
      blocks: [
        {
          kind: 'p',
          text: 'Medcor is a healthcare-access platform designed to help users discover, compare, contact and, where available, request or book services from participating healthcare providers and related service providers.',
        },
        {
          kind: 'contact',
          intro: 'Medcor is operated by:',
          lines: [
            { label: null, value: ORG.legalName },
            { label: 'Address', value: ORG.address },
          ],
        },
        {
          kind: 'p',
          text: 'Unless expressly stated otherwise for a particular service, Medcor does not itself provide medical diagnosis, treatment or clinical care. Healthcare services are provided by the relevant independent healthcare professionals, facilities or service providers.',
        },
      ],
    },
    {
      heading: '2. Medical and Emergency Use',
      blocks: [
        {
          kind: 'p',
          text: 'Information displayed through Medcor is intended to assist users in locating and accessing appropriate healthcare services. It is not a substitute for professional medical assessment, diagnosis or treatment.',
        },
        {
          kind: 'p',
          text: 'Where Medcor provides emergency-search or emergency-access functionality, the Platform assists users in identifying or contacting potentially appropriate services. Medcor does not guarantee ambulance availability, hospital capacity, specialist availability, response times or a particular clinical outcome.',
        },
        {
          kind: 'p',
          text: 'Users experiencing a medical emergency should seek appropriate emergency assistance without delaying necessary care in order to use the Platform.',
        },
      ],
    },
    {
      heading: '3. Healthcare Providers',
      blocks: [
        {
          kind: 'p',
          text: 'Healthcare providers are responsible for the accuracy of information they provide to Medcor and for the healthcare services they deliver.',
        },
        {
          kind: 'p',
          text: 'Medcor may take reasonable steps to verify participating providers, but inclusion on the Platform does not constitute a guarantee of the quality or outcome of any treatment.',
        },
        {
          kind: 'p',
          text: 'Clinical decisions remain the responsibility of appropriately qualified healthcare professionals.',
        },
      ],
    },
    {
      heading: '4. User Responsibilities',
      blocks: [
        { kind: 'p', text: 'Users must:' },
        {
          kind: 'ul',
          items: [
            'provide accurate information when using the Platform;',
            'use Medcor only for lawful purposes;',
            'not impersonate another person or submit deliberately false information;',
            'not interfere with the operation or security of the Platform; and',
            'provide health or booking information relating to another person only where they are authorised to do so.',
          ],
        },
      ],
    },
    {
      heading: '5. Bookings, Availability and Payments',
      blocks: [
        {
          kind: 'p',
          text: 'Services, prices, appointment availability and response times may be determined by individual providers and may change.',
        },
        {
          kind: 'p',
          text: 'A request submitted through Medcor does not guarantee that a healthcare provider has accepted the booking unless confirmation is provided.',
        },
        {
          kind: 'p',
          text: 'Where payment functionality is offered, applicable charges and payment conditions should be displayed before payment is completed. Third-party payment providers may also apply their own terms.',
        },
      ],
    },
    {
      heading: '6. Privacy and Personal Data',
      blocks: [
        {
          kind: 'p',
          text: 'Use of Medcor is subject to our Privacy Policy and Data Protection Statement.',
        },
        {
          kind: 'p',
          text: 'Personal information, including health-related information where necessary, will be processed in accordance with applicable Ghanaian data-protection law, including the Data Protection Act, 2012 (Act 843).',
        },
      ],
    },
    {
      heading: '7. Platform Availability',
      blocks: [
        {
          kind: 'p',
          text: 'We aim to keep Medcor reliable and available but cannot guarantee uninterrupted or error-free access. Services may occasionally be unavailable because of maintenance, connectivity failures, provider systems or circumstances outside our reasonable control.',
        },
      ],
    },
    {
      heading: '8. Liability',
      blocks: [
        {
          kind: 'p',
          text: 'Nothing in these Terms excludes or limits liability where doing so would be unlawful.',
        },
        {
          kind: 'p',
          text: 'To the extent permitted by law, Medcor is not responsible for independent clinical decisions, acts or omissions of healthcare providers or other independent third-party service providers.',
        },
        {
          kind: 'p',
          text: 'Medcor remains responsible for obligations imposed directly upon it by applicable law.',
        },
      ],
    },
    {
      heading: '9. Intellectual Property',
      blocks: [
        {
          kind: 'p',
          text: 'The Medcor name, platform design, software, branding and original content are protected by applicable intellectual-property laws. They may not be copied, modified, distributed or commercially exploited without appropriate permission.',
        },
      ],
    },
    {
      heading: '10. Suspension or Termination',
      blocks: [
        {
          kind: 'p',
          text: 'We may restrict or suspend access where reasonably necessary to protect users, healthcare providers, the Platform or its security, or where these Terms or applicable law have been materially breached.',
        },
      ],
    },
    {
      heading: '11. Changes to These Terms',
      blocks: [
        {
          kind: 'p',
          text: 'We may update these Terms where our services, legal requirements or Platform functionality change. The current version and effective date will be published on Medcor.',
        },
      ],
    },
    {
      heading: '12. Governing Law and Contact',
      blocks: [
        {
          kind: 'p',
          text: 'These Terms are governed by the laws of the Republic of Ghana.',
        },
        {
          kind: 'contact',
          intro: 'Questions concerning these Terms should be directed to:',
          lines: [
            { label: null, value: ORG.legalName },
            { label: 'Email', value: ORG.contactEmail },
            { label: 'Telephone', value: ORG.phone },
            { label: 'Address', value: ORG.address },
          ],
        },
      ],
    },
  ],
}

export const DATA_PROTECTION: LegalDocument = {
  title: 'Data Protection Statement',
  standfirst:
    'The commitments Medcor makes when handling personal and health information.',
  dated: false,
  intro: [
    {
      kind: 'p',
      text: 'Medcor recognises that healthcare information is sensitive and that users must be able to trust us with their personal information.',
    },
    {
      kind: 'p',
      text: 'We are committed to processing personal data responsibly and in accordance with the Data Protection Act, 2012 (Act 843) of Ghana and other applicable data-protection requirements.',
    },
  ],
  sections: [
    {
      heading: 'Our commitments',
      blocks: [
        { kind: 'p', text: 'In operating Medcor, we will:' },
        {
          kind: 'ul',
          items: [
            'collect personal information only where it is necessary for a defined and legitimate purpose;',
            'clearly explain why information is being collected and how it will be used;',
            'apply appropriate safeguards when processing health and other sensitive personal information;',
            'limit access to personal information to authorised persons and service providers who require it for legitimate purposes;',
            'use reasonable technical and organisational security measures to protect information against loss, unauthorised access, disclosure, alteration or destruction;',
            'retain personal information only for as long as reasonably necessary or legally required;',
            "not sell users' personal or health information;",
            'not use identifiable health information for advertising without an appropriate lawful basis;',
            'require organisations processing personal information on our behalf to apply appropriate confidentiality and data-protection safeguards; and',
            'respect applicable rights of individuals to access, correct and object to certain uses of their personal information.',
          ],
        },
        {
          kind: 'p',
          text: 'Where personal information is required to facilitate a healthcare request, Medcor will seek to disclose only information reasonably necessary for that purpose.',
        },
        {
          kind: 'p',
          text: 'Users can obtain further information about what we collect, why we collect it, who we may share it with and their rights by reading the Medcor Privacy Policy.',
        },
      ],
    },
    {
      heading: 'Contact',
      blocks: [
        {
          kind: 'contact',
          intro: 'Data-protection enquiries or requests should be directed to:',
          lines: [
            { label: null, value: ORG.legalName },
            {
              label: 'Privacy/Data Protection Contact',
              value: ORG.dataProtectionSupervisor,
            },
            { label: 'Email', value: ORG.privacyEmail },
            {
              label: 'Data Protection Commission Registration Number',
              value: ORG.dpcRegistrationNumber,
            },
          ],
          outro: [
            'Individuals who believe their personal data has been handled improperly may also exercise their rights under Ghanaian law, including making a complaint to the Data Protection Commission of Ghana.',
          ],
        },
      ],
    },
  ],
}
